<template>
  <div v-if="order" v-loading="loading">
    <div class="card shadow mb-3">
      <div class="card-header">
        <div class="row align-items-center">
          <div class="col-6">
            <nav aria-label="breadcrumb">
              <ol class="breadcrumb mb-0">
                <li class="breadcrumb-item">
                  <router-link :to="{ name: 'orders' }">Orders</router-link>
                </li>
                <li class="breadcrumb-item active" aria-current="page">
                  {{ order.reference_id }}
                </li>
              </ol>
            </nav>
          </div>
          <div class="col-6 text-right">
            <ul class="list-inline mb-0">
              <li class="list-inline-item">
                <order-confirm v-if="order.status === 'P'" :order="order" @success="getOrder" @error="getOrder" />
              </li>
              <li class="list-inline-item">
                <order-delete v-if="order.status === 'P'" :order="order" @success="$router.push({ name: 'orders' })" />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="card-body">
        <el-steps class="mb-5" :active="activeStep" align-center>
          <el-step title="Pending"></el-step>
          <el-step title="Confirm Availability"></el-step>
          <el-step title="Ship Products"></el-step>
        </el-steps>
        <dl v-if="order.user" class="row mb-0">
          <dt class="col-3">User</dt>
          <dd class="col-9">
            <router-link :to="{ name: 'users-id', params: { id: order.user.id }}">
              {{ order.user.name }}
            </router-link>
          </dd>
        </dl>
        <dl v-else class="row mb-0">
          <dt class="col-3">Email</dt>
          <dd class="col-9">
            {{ order.email }}
          </dd>
        </dl>
        <dl class="row mb-0">
          <dt class="col-3">Created</dt>
          <dd class="col-9">{{ createdAt }}</dd>
        </dl>
        <dl class="row mb-0">
          <dt class="col-3">Updated</dt>
          <dd class="col-9">{{ updatedAt }}</dd>
        </dl>
        <dl class="row mb-0">
          <dt class="col-3">Shipping Cost</dt>
          <dd class="col-9">{{ order.shipping_cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} {{ order.products[0].currency }}</dd>
        </dl>
        <dl class="row mb-0">
          <dt class="col-3">Total</dt>
          <dd class="col-9">
            {{ order.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} {{ order.products[0].currency }}
            <span v-if="order.status === 'E'" class="badge badge-danger">Error</span>
            <span class="text-danger">{{ order.error }}</span>
          </dd>
        </dl>
      </div>
    </div>
    <div class="card shadow mb-3">
      <div class="card-header">
        <div class="row align-items-center">
          <div class="col-6">
            <h2 class="h4 mb-0">Ordered Products</h2>
          </div>
          <div class="col-6 text-right">
            <order-ship v-if="order.status === 'A'" :order="order" :selectedProducts="selectedProducts" @success="getOrder" />
          </div>
        </div>
      </div>
      <table class="table">
        <thead class="thead-light">
          <tr>
            <th>
              <div class="d-flex">
                <div v-if="order.status ==='A'" class="mr-3">
                  <el-checkbox class="mb-0" :indeterminate="indeterminate" v-model="checkedAll" @change="onCheckAll"></el-checkbox>
                </div>
                <div>
                  Product
                </div>
              </div>
            </th>
            <th>Distributor</th>
            <th style="width: 15%;">Quantity</th>
            <th style="width: 15%;">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="product in order.products" :key="product.product_variant.id">
            <td>
              <div class="d-flex align-items-center">
                <div v-if="order.status ==='A'" class="mr-3">
                  <el-checkbox class="mb-0" v-model="checked[product.product_variant.id]" :disabled="product.quantity === product.quantity_shipped" @change="onCheck($event, product)"></el-checkbox>
                </div>
                <div class="d-flex">
                  <div>
                    <img class="mr-2" width="50" height="50" align="left" :src="`https://ucarecdn.com/${ product.image_id }/-/scale_crop/300x300/center/-/quality/lightest/`" style="min-width: 50px;">
                  </div>
                  <div class="flex-grow-1">
                    <a :href="`https://www.nyonyacooking.com/store/${ product.slug }~${ product.product_variant.id }`" target="_blank">{{ product.title }}</a>
                    <div class="text-muted">{{ product.product_variant.barcode.type }} {{ product.product_variant.barcode.value }}</div>
                  </div>
                </div>
              </div>
            </td>
            <td>
              <router-link :to="{ name: 'distributors-id', params: { id: product.distributor.id } }">{{ product.distributor.name }}</router-link>
              <div v-if="product.discount && product.discount.price">
                {{ product.discount.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} {{ product.currency }}
              </div>
              <div v-else>
                {{ product.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} {{ product.currency }}
              </div>
              <div class="text-muted">
                SKU {{ product.sku }}
              </div>
            </td>
            <td>
              <div><strong><span class="text-danger">{{ product.quantity }}</span></strong></div>
              <div><span class="text-danger">{{ product.quantity_shipped }}</span></div>
            </td>
            <td class="text-danger">
              {{ product.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} {{ product.currency }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="card shadow">
      <div class="card-header">
        <h2 class="h4 mb-0">Shipping Address</h2>
      </div>
      <div class="card-body">
        <dl class="row mb-0">
          <dt class="col-3">Recipient</dt>
          <dd class="col-9">{{ order.address.name }}</dd>
        </dl>
        <dl class="row mb-0">
          <dt class="col-3">Address</dt>
          <dd class="col-9">
            {{ order.address.street }}<br>
            <span v-if="order.address.building">{{ order.address.building }}<br></span>
            {{ order.address.city }} {{ order.address.state }} {{ order.address.postal_code }}<br>
            {{ order.address.country }}
          </dd>
        </dl>
        <dl class="row mb-0">
          <dt class="col-3">Phone</dt>
          <dd class="col-9">{{ order.address.phone || 'Not provided' }}</dd>
        </dl>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import OrderConfirm from '@/components/Order/OrderConfirm'
import OrderDelete from '@/components/Order/OrderDelete'
import OrderShip from '@/components/Order/OrderShip'

export default {
  components: {
    OrderConfirm,
    OrderDelete,
    OrderShip,
  },

  data() {
    return {
      loading: false,
      order: null,
      selectedProducts: [],
      checked: [],
      checkedAll: false,
      indeterminate: false,
    }
  },

  async mounted() {
    await this.getOrder()
  },

  computed: {
    activeStep() {
      if (this.order.status === 'P' || this.order.status === 'E') return 1
      if (this.order.status === 'A') return 2
      if (this.order.status === 'D') return 3
    },
    updatedAt() {
      return moment(this.order.updatedAt).format('LLLL')
    },
    createdAt() {
      return moment(this.order.createdAt).format('LLLL')
    },
  },

  methods: {
    async getOrder() {
      this.loading = true

      try {
        var { data } = await this.$http.get(`/orders/${ this.$route.params.id }`)
      } catch(err) {
        this.$message.error(err.response.data.message)
        return false
      } finally {
        this.loading = false
      }

      this.order = data
    },

    onCheck(status, payload) {
      this.checkedAll = false
      this.indeterminate = false

      if (status) {
        this.selectedProducts.push(payload)
      } else {
        this.selectedProducts = this.selectedProducts.filter(item => item.product_variant.id !== payload.product_variant.id)
      }

      if (this.selectedProducts.length === 0) {
        return
      }

      if (this.order.products.length === this.selectedProducts.length) {
        this.checkedAll = true
      } else {
        this.indeterminate = true
      }
    },

    onCheckAll(status) {
      this.indeterminate = false

      if (status) {
        this.order.products.forEach(item => {
          if (item.quantity !== item.quantity_shipped) {
            this.checked[item.product_variant.id] = true
            this.selectedProducts.push(item)
          }
        })
      } else {
        this.order.products.forEach(item => this.checked[item.product_variant.id] = false)
        this.selectedProducts = []
      }
    },
  },
}
</script>

<style>

</style>
